/**
 * AVBP Working-Calendar Resolver — Phase 1 · Sprint 3.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS
 * ─────────────────────────────────────────────────────────────────────────
 *  Sprint 2 anchored Review-Queue auto-escalation with a naïve
 *  `now + 24h` wall-clock. That is wrong for a real SLA: a proposal
 *  raised at 17:30 on a Saturday must not "escalate" at 17:30 Sunday
 *  when nobody is at a desk. The owner decision for Sprint 3 is to build
 *  a REAL working-calendar resolver: add only *business* hours, skipping
 *  the weekly off-day(s) and a configured holiday list.
 *
 *  The calendar (business hours, weekly off, holidays, timezone) is a
 *  typed CONFIG so it is tunable without touching resolver logic — this
 *  is the single source of truth and the single extension point.
 *
 *  ── Owner-CONFIRMED values (Sprint-3 revision) ───────────────────────
 *  The owner confirmed AV's real working calendar:
 *  Mon–Sat working, Sunday off, 10:00–18:00 IST (Asia/Kolkata),
 *  8 business-hours/day. "24 business hours" therefore ≈ 3 working days.
 *  If the owner ever moves to Mon–Fri, flip `weeklyOff` to [0, 6] in
 *  DEFAULT_CALENDAR — no resolver change needed.
 *
 *  The holiday list is seeded with AV's real org holidays (sourced from
 *  the HR app's Firestore `settings/orgHolidays`, project
 *  `aayatan-veda-hr`). Per memory `project_hr_into_avbp` the HR module
 *  will eventually live in THIS app, at which point that Firestore doc
 *  becomes the single live source of truth. `loadOrgHolidays()` below is
 *  the typed seam where that live read drops in — same mock-seam
 *  discipline as `applyProposalToRecord` / `assertServerSideGate`.
 * ─────────────────────────────────────────────────────────────────────────
 *
 *  TIMEZONE NOTE: this resolver operates in *wall-clock minutes within the
 *  calendar's working window* and treats the supplied JS Date in the
 *  configured IANA zone via Intl. It is deterministic and dependency-free
 *  (Node native test runner can exercise it with no DOM, no Firebase).
 */

/** 0 = Sunday … 6 = Saturday (JS getDay convention). */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface WorkingCalendar {
  /** IANA timezone the business hours are expressed in. */
  timeZone: string;
  /** Minutes past local midnight the working day opens (e.g. 600 = 10:00). */
  dayStartMin: number;
  /** Minutes past local midnight the working day closes (e.g. 1080 = 18:00). */
  dayEndMin: number;
  /** Weekdays that are NON-working (weekly off). [0] = Sundays off. */
  weeklyOff: Weekday[];
  /** Full-day holidays as `YYYY-MM-DD` strings in the calendar's zone. */
  holidays: string[];
}

/**
 * One org holiday: a full non-working calendar day in the calendar's
 * zone. Shape mirrors `aayatan-veda-hr` Firestore `settings/orgHolidays`
 * (`{ holidays: [{ date: "YYYY-MM-DD", label: string }] }`) so the live
 * loader can pass docs straight through with no remapping.
 */
export interface OrgHoliday {
  /** `YYYY-MM-DD` in the calendar's zone. */
  date: string;
  label: string;
}

/**
 * AV's real org holidays — owner-confirmed, sourced from the HR app's
 * Firestore `settings/orgHolidays` (project `aayatan-veda-hr`). Typed
 * seed constant for offline / now; superseded by `loadOrgHolidays()`
 * once the HR module lands in this app and reads the live doc.
 */
export const AV_ORG_HOLIDAYS_SEED: OrgHoliday[] = [
  { date: '2026-03-04', label: 'Holi' },
  { date: '2026-03-05', label: 'Holi' },
  { date: '2026-08-15', label: 'Independence Day' },
  { date: '2026-08-28', label: 'Rakshabandhan' },
  { date: '2026-10-20', label: 'Dussehra' },
  { date: '2026-11-08', label: 'Deepawali' },
  { date: '2026-11-09', label: 'Deepawali' },
  { date: '2026-11-10', label: 'Gowardhan Puja' },
];

/**
 * HOLIDAY-LOADER SEAM (Sprint-3 revision — returns the seed, by design).
 *
 * The single typed place a LIVE read of `aayatan-veda-hr`
 * `settings/orgHolidays` drops in once the HR module lands in this app
 * (memory `project_hr_into_avbp`) — `settings/orgHolidays` is then the
 * single source of truth. Identical discipline to `assertServerSideGate`
 * / `applyProposalToRecord`: NO cross-project Firestore read is wired
 * now (no deploy / no rules work). Returns the owner-confirmed seed so
 * `DEFAULT_CALENDAR` is correct offline today. DO NOT inline a Firestore
 * read elsewhere — replace the body here when the HR module ships.
 */
export async function loadOrgHolidays(): Promise<OrgHoliday[]> {
  // SEAM: real read of aayatan-veda-hr `settings/orgHolidays` lands with
  // the in-app HR module sprint. Keep returning the seed until then.
  return AV_ORG_HOLIDAYS_SEED;
}

/**
 * AV default — owner-confirmed. Mon–Sat working, Sunday off,
 * 10:00–18:00 IST, 8 business-hours per working day. Holidays seeded
 * from AV's real org holiday list. SWAP THIS ONE OBJECT to retune.
 */
export const DEFAULT_CALENDAR: WorkingCalendar = {
  timeZone: 'Asia/Kolkata',
  dayStartMin: 10 * 60, // 10:00
  dayEndMin: 18 * 60, // 18:00
  weeklyOff: [0], // Sunday
  holidays: AV_ORG_HOLIDAYS_SEED.map((h) => h.date),
};

/** Business minutes available in one full working day. */
export function minutesPerWorkingDay(cal: WorkingCalendar): number {
  return cal.dayEndMin - cal.dayStartMin;
}

/* ── zone-aware date parts (no external dep) ── */

interface Parts {
  y: number;
  mo: number;
  d: number;
  min: number; // minutes past local midnight
  dow: Weekday;
}

function partsInZone(date: Date, tz: string): Parts {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short',
  });
  const p: Record<string, string> = {};
  for (const part of fmt.formatToParts(date)) p[part.type] = part.value;
  const dowMap: Record<string, Weekday> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  let hour = parseInt(p.hour, 10);
  if (hour === 24) hour = 0; // some ICU builds emit "24" at midnight
  return {
    y: parseInt(p.year, 10),
    mo: parseInt(p.month, 10),
    d: parseInt(p.day, 10),
    min: hour * 60 + parseInt(p.minute, 10),
    dow: dowMap[p.weekday],
  };
}

function ymd(p: Parts): string {
  const mm = String(p.mo).padStart(2, '0');
  const dd = String(p.d).padStart(2, '0');
  return `${p.y}-${mm}-${dd}`;
}

function isWorkingDay(p: Parts, cal: WorkingCalendar): boolean {
  if (cal.weeklyOff.includes(p.dow)) return false;
  if (cal.holidays.includes(ymd(p))) return false;
  return true;
}

/**
 * Advance `from` by `businessHours` of working time and return the
 * resulting Date. Time that falls outside the working window, on a
 * weekly-off day, or on a holiday does NOT count toward the budget.
 *
 * Deterministic; pure; no side effects. Stepping is done in whole
 * minutes inside the calendar's zone, so DST / zone math is handled by
 * Intl rather than by manual offset arithmetic.
 */
export function addBusinessHours(
  from: Date,
  businessHours: number,
  cal: WorkingCalendar = DEFAULT_CALENDAR,
): Date {
  let remainingMin = Math.max(0, Math.round(businessHours * 60));
  if (remainingMin === 0) return new Date(from.getTime());

  // Step in 1-minute ticks but skip whole non-working spans in bulk so
  // a Friday-evening proposal doesn't loop minute-by-minute through the
  // weekend. We jump to the next working-window open when outside it.
  let cursor = from.getTime();
  // Safety bound: never iterate more than ~2 calendar years of steps.
  const HARD_STOP = from.getTime() + 1000 * 60 * 60 * 24 * 800;

  while (remainingMin > 0 && cursor < HARD_STOP) {
    const p = partsInZone(new Date(cursor), cal.timeZone);

    if (!isWorkingDay(p, cal)) {
      cursor = nextLocalMidnightMs(cursor, cal.timeZone);
      continue;
    }
    if (p.min < cal.dayStartMin) {
      // before opening — jump to today's open
      cursor += (cal.dayStartMin - p.min) * 60_000;
      continue;
    }
    if (p.min >= cal.dayEndMin) {
      // after closing — jump to next local midnight, loop re-evaluates
      cursor = nextLocalMidnightMs(cursor, cal.timeZone);
      continue;
    }
    // inside the working window: consume up to end-of-day
    const availToday = cal.dayEndMin - p.min;
    const consume = Math.min(availToday, remainingMin);
    cursor += consume * 60_000;
    remainingMin -= consume;
  }
  return new Date(cursor);
}

/** ms of the next local-midnight boundary in `tz` strictly after `ms`. */
function nextLocalMidnightMs(ms: number, tz: string): number {
  const p = partsInZone(new Date(ms), tz);
  const minsIntoDay = p.min;
  // minutes left to reach 24:00 local, +1 min to land on the next day
  const toMidnight = 24 * 60 - minsIntoDay;
  return ms + toMidnight * 60_000;
}

/**
 * Whole business hours remaining between `now` and a target Date,
 * counting only working time. Used for the Review-Queue countdown
 * ("escalates in Nh"). Returns 0 once the target is reached/passed.
 */
export function businessHoursUntil(
  target: Date,
  now: Date = new Date(),
  cal: WorkingCalendar = DEFAULT_CALENDAR,
): number {
  if (target.getTime() <= now.getTime()) return 0;
  let remaining = 0;
  let cursor = now.getTime();
  const end = target.getTime();
  const HARD_STOP = now.getTime() + 1000 * 60 * 60 * 24 * 800;

  while (cursor < end && cursor < HARD_STOP) {
    const p = partsInZone(new Date(cursor), cal.timeZone);
    if (!isWorkingDay(p, cal)) {
      cursor = Math.min(end, nextLocalMidnightMs(cursor, cal.timeZone));
      continue;
    }
    if (p.min < cal.dayStartMin) {
      cursor = Math.min(end, cursor + (cal.dayStartMin - p.min) * 60_000);
      continue;
    }
    if (p.min >= cal.dayEndMin) {
      cursor = Math.min(end, nextLocalMidnightMs(cursor, cal.timeZone));
      continue;
    }
    const availToday = cal.dayEndMin - p.min;
    const span = Math.min(availToday * 60_000, end - cursor);
    remaining += span;
    cursor += span;
  }
  return Math.round(remaining / 3_600_000);
}
